import { z } from "zod";

export const DaRuleSchema = z.object({
  id: z.number().int(),
  heading: z.string(),
  level: z.number().int().min(1).max(6),
  sortOrder: z.number().int(),
});
export type DaRule = z.infer<typeof DaRuleSchema>;

/**
 * Formats each imported heading with its parent headings, preserving document order.
 * A Section → Title → Rule path reads as "Section 1, Title 3 - Respect".
 */
export function formatDaRuleLabels(rules: readonly DaRule[]): Map<number, string> {
  const labels = new Map<number, string>();
  const ancestors: Array<{ level: number; heading: string }> = [];
  const ordered = [...rules].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);

  for (const rule of ordered) {
    while (ancestors.length && ancestors[ancestors.length - 1]!.level >= rule.level) {
      ancestors.pop();
    }

    const parentHeadings = ancestors.map((ancestor) => ancestor.heading);
    const label =
      parentHeadings.length > 1
        ? `${parentHeadings.join(", ")} - ${rule.heading}`
        : parentHeadings.length === 1
          ? `${parentHeadings[0]} - ${rule.heading}`
          : rule.heading;

    labels.set(rule.id, label);
    ancestors.push({ level: rule.level, heading: rule.heading });
  }

  return labels;
}

export const DaRuleCatalogSchema = z.object({
  documentId: z.string().nullable(),
  documentTitle: z.string().nullable(),
  importedAt: z.string().nullable(),
  rules: z.array(DaRuleSchema),
});
export type DaRuleCatalog = z.infer<typeof DaRuleCatalogSchema>;

export const ImportDaRulesBodySchema = z.object({
  documentUrl: z.string().trim().url().max(2048),
});
export type ImportDaRulesBody = z.infer<typeof ImportDaRulesBodySchema>;
